import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface MentorStudent {
  id: string;
  full_name: string;
  email: string;
  avatar_url: string | null;
  company: string | null;
  position: string | null;
  linkedin_url: string | null;
  bio: string | null;
  last_login: string | null;
  cohort_id: string;
  cohort_name: string;
  organization_id: string | null;
  organization_name: string | null;
  enrolled_at: string | null;
  content_progress: number;
  deliverables_count: number;
  project_name: string | null;
  project_status: string | null;
  status: 'active' | 'at-risk' | 'inactive';
}

export interface MentorCohort {
  id: string;
  name: string;
  organization_id: string | null;
  organization_name: string | null;
  status: string | null;
}

export function useMentorCohorts() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['mentor-cohorts', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cohort_mentors')
        .select(`
          cohort_id,
          cohorts (
            id, name, status, organization_id, is_perpetual,
            organizations ( id, name )
          )
        `)
        .eq('user_id', user!.id);
      if (error) throw error;

      // Las rutas de autoaprendizaje (cohortes perpetuas) no son cohortes con
      // acompañamiento: no deben aparecer como cohortes del mentor.
      return (data ?? [])
        .filter((row: any) => row.cohorts && row.cohorts.is_perpetual !== true)
        .map((row: any) => ({
        id: row.cohorts.id,
        name: row.cohorts.name,
        organization_id: row.cohorts.organization_id,
        organization_name: row.cohorts.organizations?.name ?? null,
        status: row.cohorts.status,
      })) as MentorCohort[];
    },
    enabled: !!user?.id,
  });
}

export function useMentorStudents(cohortIds: string[]) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['mentor-students', user?.id, cohortIds],
    queryFn: async () => {
      if (cohortIds.length === 0) return [];

      // 1. Get participants with profiles
      const { data: participants, error: pErr } = await supabase
        .from('cohort_participants')
        .select(`
          user_id, cohort_id, enrolled_at,
          cohorts ( id, name, organization_id, organizations ( id, name ) ),
          profiles ( id, full_name, email, avatar_url, company, position, linkedin_url, bio, last_login )
        `)
        .in('cohort_id', cohortIds);
      if (pErr) throw pErr;
      if (!participants || participants.length === 0) return [];

      const userIds = participants.map((p: any) => p.user_id);

      // 2. Content progress per user, measured against the cohort's required curriculum
      const { data: progressData } = await (supabase as any).rpc('get_users_curriculum_progress', {
        _user_ids: Array.from(new Set(userIds)),
        _cohort_ids: cohortIds,
      });

      const progressMap: Record<string, { total: number; completed: number }> = {};
      ((progressData ?? []) as any[]).forEach((row) => {
        progressMap[row.user_id] = { total: row.total_required ?? 0, completed: row.completed_required ?? 0 };
      });

      // 3. Deliverables count per user
      const { data: delivData } = await supabase
        .from('participant_deliverables')
        .select('user_id')
        .in('user_id', userIds);

      const delivMap: Record<string, number> = {};
      (delivData ?? []).forEach((row: any) => {
        delivMap[row.user_id] = (delivMap[row.user_id] || 0) + 1;
      });

      // 4. Latest project per user
      const { data: projData } = await supabase
        .from('participant_projects')
        .select('user_id, name, status')
        .in('user_id', userIds)
        .order('created_at', { ascending: false });

      const projMap: Record<string, { name: string; status: string | null }> = {};
      (projData ?? []).forEach((row: any) => {
        if (!projMap[row.user_id]) projMap[row.user_id] = { name: row.name, status: row.status };
      });

      // 5. Build student records
      const now = new Date();
      return participants.map((p: any): MentorStudent => {
        const profile = p.profiles;
        const cohort = p.cohorts;
        const prog = progressMap[p.user_id];
        const contentProgress = prog && prog.total > 0
          ? Math.round((prog.completed / prog.total) * 100)
          : 0;
        const delivCount = delivMap[p.user_id] || 0;
        const project = projMap[p.user_id];

        // Determine status
        let status: 'active' | 'at-risk' | 'inactive' = 'active';
        const lastLogin = profile?.last_login ? new Date(profile.last_login) : null;
        const daysSinceLogin = lastLogin ? Math.floor((now.getTime() - lastLogin.getTime()) / (1000 * 60 * 60 * 24)) : 999;

        if (daysSinceLogin > 14) {
          status = 'inactive';
        } else if (contentProgress < 30 && delivCount === 0) {
          status = 'at-risk';
        }

        return {
          id: p.user_id,
          full_name: profile?.full_name ?? '',
          email: profile?.email ?? '',
          avatar_url: profile?.avatar_url ?? null,
          company: profile?.company ?? null,
          position: profile?.position ?? null,
          linkedin_url: profile?.linkedin_url ?? null,
          bio: profile?.bio ?? null,
          last_login: profile?.last_login ?? null,
          cohort_id: p.cohort_id,
          cohort_name: cohort?.name ?? '',
          organization_id: cohort?.organization_id ?? null,
          organization_name: cohort?.organizations?.name ?? null,
          enrolled_at: p.enrolled_at,
          content_progress: contentProgress,
          deliverables_count: delivCount,
          project_name: project?.name ?? null,
          project_status: project?.status ?? null,
          status,
        };
      });
    },
    enabled: !!user?.id && cohortIds.length > 0,
  });
}
